// 専用 材料マスタ画面 (モックアップ rev2.8〜 を Web へ移植)。
// 材料一覧をアコーディオン表示し、各材料の細目を展開内で CRUD する。
// 特殊ルール:
//   - 新規材料作成時に「本体」細目をデフォルトで1件自動追加
//   - 材料の細目が1件しか無い時、最後の細目を削除不可
//   - QR管理フラグは材料単位 (細目には持たない)

import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Stack, Typography, Paper, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Switch, FormControlLabel, MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
  Accordion, AccordionDetails, Chip, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { replaceCollection, replaceCollections, nextIdFor, useFullDb } from '../../../api/dbSlice';
import { MasterAccordionSummary } from './MasterAccordionSummary';
import { useIsOnline } from '../../../util/useIsOnline';
import { OfflineMasterBanner } from '../../../components/OfflineMasterBanner';
import { usePartitionFilter } from '../../../util/partitionFilterStore';
import type { MaterialMasterBase } from '../../../schema/generated/MaterialMaster';
import type { MaterialDetailBase } from '../../../schema/generated/MaterialDetail';
import { PageContext } from '../PageContext';
import { WORD } from '../../../word';
import { renderWord } from '../../../util/renderWord';
import Time from '../../../util/Time';
import { EmptyState } from '../../../components/EmptyState';
import { errorMessageOf } from "../../../util/typed";
import { ContentLoading } from "../../../components/Loading";

type Material = MaterialMasterBase;
type MaterialDetail = MaterialDetailBase;

// ---- 材料細目の編集ダイアログ ----
function DetailEditDialog({ open, value, isOnline, onClose, onSave }: {
  open: boolean;
  value: MaterialDetail | null;
  isOnline: boolean;
  onClose: () => void;
  onSave: (next: MaterialDetail) => void;
}) {
  const [draft, setDraft] = useState<MaterialDetail | null>(null);
  useEffect(() => { setDraft(value ? { ...value } : null); }, [value]);
  if (!draft) return null;
  const update = <K extends keyof MaterialDetail>(k: K, v: MaterialDetail[K]) => setDraft({ ...draft, [k]: v });
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>材料細目の編集</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField
            label="細目名" size="small" fullWidth required
            value={draft.detail_name ?? ''} onChange={(e) => update('detail_name', e.target.value)}
            helperText={WORD.materialMaster.hDetailName}
          />
          <Stack direction="row" gap={2}>
            <TextField
              label="荷姿" size="small" fullWidth value={draft.package_form ?? ''}
              onChange={(e) => update('package_form', e.target.value)}
            />
            <TextField
              label="使用期限月数" size="small" type="number"
              value={draft.shelf_life_months ?? ''}
              onChange={(e) => update('shelf_life_months', e.target.value === '' ? null : Number(e.target.value))}
              sx={{ minWidth: 160 }}
            />
          </Stack>
          <Stack direction="row" gap={2}>
            <TextField
              label="内容量" size="small" type="number"
              value={draft.content_quantity ?? ''}
              onChange={(e) => update('content_quantity', e.target.value === '' ? null : Number(e.target.value))}
            />
            <TextField
              label="容量単位" size="small" required value={draft.content_unit ?? ''}
              onChange={(e) => update('content_unit', e.target.value)}
              helperText={WORD.materialMaster.hContentUnit}
            />
            <TextField
              label="ソート順" size="small" type="number"
              value={draft.sort_order ?? 99}
              onChange={(e) => update('sort_order', Number(e.target.value))}
            />
          </Stack>
          <Stack direction="row" gap={2}>
            <TextField
              label="比重" size="small" type="number"
              value={draft.specific_gravity ?? ''}
              onChange={(e) => update('specific_gravity', e.target.value === '' ? null : Number(e.target.value))}
            />
            <TextField
              label="危険物分類" size="small" fullWidth value={draft.hazardous_class ?? ''}
              onChange={(e) => update('hazardous_class', e.target.value === '' ? null : e.target.value)}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button variant="contained" onClick={() => onSave(draft)} disabled={!isOnline || !draft.detail_name || !draft.content_unit}>保存</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- 材料の編集ダイアログ ----
function MaterialEditDialog({ open, value, isNew, isOnline, onClose, onSave }: {
  open: boolean;
  value: Material | null;
  isNew: boolean;
  isOnline: boolean;
  onClose: () => void;
  onSave: (next: Material) => void;
}) {
  const [draft, setDraft] = useState<Material | null>(null);
  useEffect(() => { setDraft(value ? { ...value } : null); }, [value]);
  if (!draft) return null;
  const update = <K extends keyof Material>(k: K, v: Material[K]) => setDraft({ ...draft, [k]: v });
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{isNew ? '材料を追加' : '材料を編集'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField label="材料名" size="small" fullWidth required
            value={draft.name ?? ''} onChange={(e) => update('name', e.target.value)} />
          <Stack direction="row" gap={2}>
            <TextField label="用途" size="small" fullWidth value={draft.usage_category ?? ''}
              onChange={(e) => update('usage_category', e.target.value)} helperText={WORD.materialMaster.hUsageCategory} />
            <TextField label="メーカー" size="small" fullWidth value={draft.manufacturer ?? ''}
              onChange={(e) => update('manufacturer', e.target.value)} />
          </Stack>
          <TextField label="規格名" size="small" fullWidth value={draft.standard_name ?? ''}
            onChange={(e) => update('standard_name', e.target.value)} />
          <TextField label="荷姿" size="small" fullWidth value={draft.package_form ?? ''}
            onChange={(e) => update('package_form', e.target.value)} helperText={WORD.materialMaster.hPackageForm} />
          <Box>
            <FormControlLabel
              control={<Switch checked={!!draft.qr_managed} onChange={(e) => update('qr_managed', e.target.checked)} />}
              label="QR 個体管理する"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {WORD.materialMaster.hQrManaged}
            </Typography>
          </Box>

          <Typography variant="subtitle2" mt={1}>管理値（未入力＝判定対象外）</Typography>
          <Typography variant="caption" color="text.secondary">温度の許容範囲・温度別可使時間は「可使時間マスタ」で管理します。</Typography>
          <Stack direction="row" gap={2}>
            <TextField label="湿度 下限 (%)" size="small" type="number"
              value={draft.humidity_lower ?? ''}
              onChange={(e) => update('humidity_lower', e.target.value === '' ? null : Number(e.target.value))} />
            <TextField label="湿度 上限 (%)" size="small" type="number"
              value={draft.humidity_upper ?? ''}
              onChange={(e) => update('humidity_upper', e.target.value === '' ? null : Number(e.target.value))} />
          </Stack>
          <Typography variant="subtitle2" mt={1}>標準使用量（メーカー参考値・閲覧専用）</Typography>
          <Stack direction="row" gap={2}>
            <TextField label="標準使用量" size="small" type="number"
              value={draft.standard_usage ?? ''}
              onChange={(e) => update('standard_usage', e.target.value === '' ? null : Number(e.target.value))}
              helperText={WORD.materialMaster.hStandardUsage} />
            <TextField label="標準使用量 単位" size="small" value={draft.standard_usage_unit ?? ''}
              onChange={(e) => update('standard_usage_unit', e.target.value === '' ? null : e.target.value)} helperText={WORD.materialMaster.hStandardUsageUnit} />
          </Stack>
          <TextField label="備考" size="small" fullWidth multiline minRows={2}
            value={draft.remarks ?? ''}
            onChange={(e) => update('remarks', e.target.value === '' ? null : e.target.value)}
            helperText={WORD.materialMaster.hRemarks} />

          <Stack direction="row" gap={2}>
            <TextField label="表示順" size="small" type="number"
              value={draft.display_order ?? 99}
              onChange={(e) => update('display_order', Number(e.target.value))} />
            <FormControlLabel
              control={<Switch checked={!!draft.is_active} onChange={(e) => update('is_active', e.target.checked)} />}
              label="有効"
            />
          </Stack>

          {isNew && (
            <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.materialMaster.materialDialogInfo)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
        <Button variant="contained" onClick={() => onSave(draft)} disabled={!isOnline || !draft.name}>保存</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- メインページ ----
export default function MaterialMasterPage({ pageContext, showOfflineBanner = true }: { pageContext: PageContext; showOfflineBanner?: boolean }) {
  const idb = pageContext.api.indexedDB;
  const ownerId = pageContext.api.currentUser.id;
  const { db, loading } = useFullDb(idb, ownerId);
  const isOnline = useIsOnline(pageContext.api);

  const allMaterials = (db.materials ?? []).filter((m) => m.deleted_at == null);
  const materialDetails = (db.materialDetails ?? []).filter((d) => d.deleted_at == null);

  const partitionOptions = useMemo(() => {
    return db.materialDivisions
      .filter((d) => d.is_active && d.deleted_at == null)
      .sort((a, b) => a.display_order - b.display_order)
      .map((d) => ({ value: d.id, label: `#${d.id} ${d.name}` }));
  }, [db.materialDivisions]);

  const [filterValue, setFilterValue] = usePartitionFilter('material_division_id');
  useEffect(() => {
    if (partitionOptions.length === 0) return;
    if (filterValue === null || !partitionOptions.some((o) => o.value === filterValue)) {
      const firstOption = partitionOptions[0];
      if (firstOption !== undefined) {
        setFilterValue(firstOption.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partitionOptions]);

  const visibleMaterials = useMemo(
    () => {
      if (filterValue == null) {
        return allMaterials;
      }
      return allMaterials.filter((m) => m.material_division_id === filterValue);
    },
    [allMaterials, filterValue]
  );

  // ---- 編集ステート ----
  const [editingMaterial, setEditingMaterial] = useState<Material | null>(null);
  const [editingIsNew, setEditingIsNew] = useState(false);
  const [confirmDeleteMaterial, setConfirmDeleteMaterial] = useState<Material | null>(null);
  const [editingDetail, setEditingDetail] = useState<MaterialDetail | null>(null);
  const [confirmDeleteDetail, setConfirmDeleteDetail] = useState<MaterialDetail | null>(null);

  // ---- 材料 CRUD ----
  const openNewMaterial = async () => {
    const newMaterialId = await nextIdFor(idb, ownerId, 'materials');
    const now = Time.Now().GetTime();
    const initial: Material = {
      id: newMaterialId,
      createdAt: now,
      updatedAt: now,
      deleted_at: null,
      material_division_id: filterValue ?? 1,
      name: '',
      usage_category: '',
      standard_name: '',
      manufacturer: '',
      package_form: '',
      display_order: 99,
      is_active: true,
      qr_managed: true,
      humidity_upper: null,
      humidity_lower: null,
      standard_usage: null,
      standard_usage_unit: null,
      remarks: null,
    };
    setEditingMaterial(initial);
    setEditingIsNew(true);
  };
  const openEditMaterial = (m: Material) => { setEditingMaterial({ ...m }); setEditingIsNew(false); };
  const closeMaterialDialog = () => { setEditingMaterial(null); setEditingIsNew(false); };

  const saveMaterial = async (next: Material) => {
    try {
      const now = Time.Now().GetTime();
      const curMaterials = (db.materials ?? []);
      const exists = curMaterials.some((m) => m.id === next.id);
      const nextWithTs: Material = { ...next, updatedAt: now };
      if (exists) {
        const nextMaterials = curMaterials.map((m) => m.id === next.id ? nextWithTs : m);
        await replaceCollection(idb, ownerId, 'materials', nextMaterials, pageContext.api);
      } else {
        const newMaterialsList = [...curMaterials, nextWithTs];
        // 新規時はデフォルト細目1件を自動追加
        const curDetails = (db.materialDetails ?? []);
        const detailId = await nextIdFor(idb, ownerId, 'materialDetails');
        const defaultDetail: MaterialDetail = {
          id: detailId,
          createdAt: now,
          updatedAt: now,
          deleted_at: null,
          material_division_id: next.material_division_id,
          material_id: next.id,
          detail_name: '本体',
          package_form: '',
          shelf_life_months: null,
          content_quantity: null,
          content_unit: 'kg',
          sort_order: 1,
          specific_gravity: null,
          hazardous_class: null,
        };
        const nextDetails = [...curDetails, defaultDetail];
        await replaceCollections(idb, ownerId, { materials: newMaterialsList, materialDetails: nextDetails }, pageContext.api);
      }
      closeMaterialDialog();
    } catch (error) {
      pageContext.api.showError(`材料の保存に失敗しました。\n${errorMessageOf(error)}`);
    }
  };

  // 材料の複製: 材料 + その細目すべてを複製
  const duplicateMaterial = async (m: Material) => {
    try {
      const now = Time.Now().GetTime();
      const curMaterials = (db.materials ?? []);
      const newMatId = await nextIdFor(idb, ownerId, 'materials');
      const newMat: Material = { ...m, id: newMatId, name: `${m.name} (コピー)`, createdAt: now, updatedAt: now, deleted_at: null };
      const nextMaterials = [...curMaterials, newMat];
      const curDetails = (db.materialDetails ?? []);
      const origDetails = curDetails.filter((d) => d.material_id === m.id && d.deleted_at == null);
      const detailIdBase = await nextIdFor(idb, ownerId, 'materialDetails');
      const newDetails: MaterialDetail[] = origDetails.map((d, i) => ({
        ...d, id: detailIdBase + i, material_id: newMatId, createdAt: now, updatedAt: now, deleted_at: null,
      }));
      const nextDetails = [...curDetails, ...newDetails];
      await replaceCollections(idb, ownerId, { materials: nextMaterials, materialDetails: nextDetails }, pageContext.api);
    } catch (error) {
      pageContext.api.showError(`材料の複製に失敗しました。\n${errorMessageOf(error)}`);
    }
  };

  // 材料の削除（論理削除）: 材料 + 細目もまとめて deleted_at=now に
  const deleteMaterial = async (m: Material) => {
    try {
      const now = Time.Now().GetTime();
      const nextMaterials = ((db.materials ?? [])).map((x) =>
        x.id === m.id ? { ...x, deleted_at: now, updatedAt: now } : x
      );
      const nextDetails = ((db.materialDetails ?? [])).map((d) =>
        d.material_id === m.id ? { ...d, deleted_at: now, updatedAt: now } : d
      );
      await replaceCollections(idb, ownerId, { materials: nextMaterials, materialDetails: nextDetails }, pageContext.api);
      setConfirmDeleteMaterial(null);
    } catch (error) {
      pageContext.api.showError(`材料の削除に失敗しました。\n${errorMessageOf(error)}`);
    }
  };

  // ---- 細目 CRUD ----
  const openNewDetail = async (materialId: number) => {
    const m = allMaterials.find((x) => x.id === materialId);
    const newDetailId = await nextIdFor(idb, ownerId, 'materialDetails');
    const now = Time.Now().GetTime();
    const initial: MaterialDetail = {
      id: newDetailId,
      createdAt: now,
      updatedAt: now,
      deleted_at: null,
      material_division_id: m?.material_division_id ?? 1,
      material_id: materialId,
      detail_name: '',
      package_form: '',
      shelf_life_months: null,
      content_quantity: null,
      content_unit: 'kg',
      sort_order: 99,
      specific_gravity: null,
      hazardous_class: null,
    };
    setEditingDetail(initial);
  };
  const openEditDetail = (d: MaterialDetail) => {
    setEditingDetail({ ...d });
  };
  const closeDetailDialog = () => {
    setEditingDetail(null);
  };

  const saveDetail = async (next: MaterialDetail) => {
    try {
      const now = Time.Now().GetTime();
      const nextWithTs: MaterialDetail = { ...next, updatedAt: now };
      const cur = (db.materialDetails ?? []);
      const nextDetails = cur.some((d) => d.id === next.id)
        ? cur.map((d) => d.id === next.id ? nextWithTs : d)
        : [...cur, nextWithTs];
      await replaceCollection(idb, ownerId, 'materialDetails', nextDetails, pageContext.api);
      closeDetailDialog();
    } catch (error) {
      pageContext.api.showError(`材料細目の保存に失敗しました。\n${errorMessageOf(error)}`);
    }
  };

  // 細目削除: 当該材料の残細目が1件しか無い場合は削除拒否
  const deleteDetail = async (d: MaterialDetail) => {
    const remaining = materialDetails.filter((x) => x.material_id === d.material_id && x.id !== d.id);
    if (remaining.length === 0) {
      setConfirmDeleteDetail(null);
      return;
    }
    try {
      const now = Time.Now().GetTime();
      const nextDetails = ((db.materialDetails ?? [])).map((x) =>
        x.id === d.id ? { ...x, deleted_at: now, updatedAt: now } : x
      );
      await replaceCollection(idb, ownerId, 'materialDetails', nextDetails, pageContext.api);
      setConfirmDeleteDetail(null);
    } catch (error) {
      pageContext.api.showError(`材料細目の削除に失敗しました。\n${errorMessageOf(error)}`);
    }
  };

  const duplicateDetail = async (d: MaterialDetail) => {
    try {
      const now = Time.Now().GetTime();
      const curDetails = (db.materialDetails ?? []);
      const newDetailId = await nextIdFor(idb, ownerId, 'materialDetails');
      const copy: MaterialDetail = { ...d, id: newDetailId, detail_name: `${d.detail_name} (コピー)`, createdAt: now, updatedAt: now, deleted_at: null };
      await replaceCollection(idb, ownerId, 'materialDetails', [...curDetails, copy], pageContext.api);
    } catch (error) {
      pageContext.api.showError(`材料細目の複製に失敗しました。\n${errorMessageOf(error)}`);
    }
  };

  // ---- 行のアコーディオン状態 ----
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const handleExpand = (id: number) => setExpandedId((prev) => prev === id ? null : id);

  if (loading) {
    return <ContentLoading />;
  }

  return (
    // 余白と最大幅は親のマスター編集画面が持つため、ここでは内側の並びだけを指定する。
    <Stack gap={2}>
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Typography variant="body2" color="text.secondary">材料区分 で絞り込み:</Typography>
        <TextField
          select size="small" value={filterValue ?? ''}
          onChange={(e) => setFilterValue(e.target.value === '' ? null : Number(e.target.value))}
          sx={{ minWidth: 220 }}
        >
          {partitionOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <Typography variant="caption" color="text.secondary">
          （{visibleMaterials.length} / 全 {allMaterials.length} 件）
        </Typography>
        <Box flex={1} />
        <Button variant="contained" startIcon={<AddIcon />} disabled={!isOnline} onClick={openNewMaterial}>新規追加</Button>
      </Stack>

      {showOfflineBanner && <OfflineMasterBanner isOnline={isOnline} />}

      <Alert severity="info" sx={{ py: 0.5 }}>{renderWord(WORD.materialMaster.listInfo)}</Alert>

      <Paper variant="outlined">
        <Box sx={{ overflowX: 'auto' }}>
          {visibleMaterials.length === 0 && (
            <EmptyState message='該当する材料がありません。「新規追加」から登録してください。' py={4} />
          )}
          {visibleMaterials.map((m) => {
            const details = materialDetails
              .filter((d) => d.material_id === m.id)
              .sort((a, b) => a.sort_order - b.sort_order);
            const isLastDetail = details.length <= 1;
            return (
              <Accordion key={m.id} disableGutters expanded={expandedId === m.id} onChange={() => handleExpand(m.id)}>
                <MasterAccordionSummary
                  idLabel={`#${m.id}`}
                  title={<Typography variant="body2" fontWeight={600} sx={{ minWidth: 200 }}>{m.name}</Typography>}
                  chips={<>
                    <Chip
                      size="small"
                      color={m.qr_managed ? 'primary' : 'default'}
                      variant={m.qr_managed ? 'filled' : 'outlined'}
                      label={m.qr_managed ? 'QR' : '副資材'}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 120 }}>
                      {m.usage_category || '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{m.manufacturer || '-'}</Typography>
                    <Box flex={1} />
                    <Stack direction="row" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                      {details.map((d) => (
                        <Chip
                          key={d.id} size="small"
                          variant="outlined"
                          label={d.detail_name}
                        />
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {details.length} 細目
                    </Typography>
                  </>}
                  actions={<>
                    <IconButton size="small" title="編集" disabled={!isOnline} onClick={() => openEditMaterial(m)}><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" title="複製" disabled={!isOnline} onClick={() => duplicateMaterial(m)}><ContentCopyIcon fontSize="small" /></IconButton>
                    <IconButton size="small" title="削除" disabled={!isOnline} onClick={() => setConfirmDeleteMaterial(m)}><DeleteIcon fontSize="small" /></IconButton>
                  </>}
                />
                <AccordionDetails sx={{ backgroundColor: 'grey.50' }}>
                  <Stack gap={1.5}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="subtitle2">材料細目 ({details.length} 件)</Typography>
                      <Box flex={1} />
                      <Button size="small" startIcon={<AddIcon />} disabled={!isOnline} onClick={() => openNewDetail(m.id)}>細目を追加</Button>
                    </Stack>
                    <Paper variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', width: 60 }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>細目名</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 110 }}>内容量</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 80 }}>荷姿</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 100 }}>使用期限</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', width: 130 }}>操作</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {details.map((d) => (
                            <TableRow key={d.id}>
                              <TableCell>{d.id}</TableCell>
                              <TableCell>{d.detail_name}</TableCell>
                              <TableCell>{d.content_quantity == null ? '-' : `${d.content_quantity} ${d.content_unit}`}</TableCell>
                              <TableCell>{d.package_form || '-'}</TableCell>
                              <TableCell>{d.shelf_life_months ?? '-'}</TableCell>
                              <TableCell>
                                <IconButton size="small" title="編集" disabled={!isOnline} onClick={() => openEditDetail(d)}><EditIcon fontSize="small" /></IconButton>
                                <IconButton size="small" title="複製" disabled={!isOnline} onClick={() => duplicateDetail(d)}><ContentCopyIcon fontSize="small" /></IconButton>
                                <Tooltip title={isLastDetail ? '最後の細目は削除できません' : ''}>
                                  <span>
                                    <IconButton size="small" title="削除" disabled={isLastDetail || !isOnline}
                                      onClick={() => setConfirmDeleteDetail(d)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}
                          {details.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                細目がありません
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      </Paper>

      <MaterialEditDialog
        open={!!editingMaterial}
        value={editingMaterial}
        isNew={editingIsNew}
        isOnline={isOnline}
        onClose={closeMaterialDialog}
        onSave={saveMaterial}
      />

      <DetailEditDialog
        open={!!editingDetail}
        value={editingDetail}
        isOnline={isOnline}
        onClose={closeDetailDialog}
        onSave={saveDetail}
      />

      {/* 材料削除確認 */}
      <Dialog open={!!confirmDeleteMaterial} onClose={() => setConfirmDeleteMaterial(null)} fullWidth maxWidth="sm">
        <DialogTitle>材料の削除確認</DialogTitle>
        <DialogContent>
          {confirmDeleteMaterial && (() => {
            const target = confirmDeleteMaterial;
            const details = materialDetails.filter((d) => d.material_id === target.id);
            return (
              <Stack gap={1}>
                <Typography>材料「<b>#{target.id} {target.name}</b>」を論理削除します。</Typography>
                {details.length > 0 && (
                  <Typography color="warning.main">
                    紐づく細目 <b>{details.length} 件</b> もまとめて論理削除されます。
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  論理削除のためデータは内部に残置されます（ID は再利用されません）。
                </Typography>
              </Stack>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteMaterial(null)}>キャンセル</Button>
          <Button color="error" variant="contained" disabled={!isOnline} onClick={() => confirmDeleteMaterial && deleteMaterial(confirmDeleteMaterial)}>削除</Button>
        </DialogActions>
      </Dialog>

      {/* 細目削除確認 */}
      <Dialog open={!!confirmDeleteDetail} onClose={() => setConfirmDeleteDetail(null)}>
        <DialogTitle>細目の削除確認</DialogTitle>
        <DialogContent>
          {confirmDeleteDetail && (
            <Typography>
              細目「<b>{confirmDeleteDetail.detail_name}</b>」を論理削除します。よろしいですか？
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDetail(null)}>キャンセル</Button>
          <Button color="error" variant="contained" disabled={!isOnline} onClick={() => confirmDeleteDetail && deleteDetail(confirmDeleteDetail)}>削除</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
